import {
  type CyberAssessmentAnswerSelectDto,
  type CyberAssessmentSelectDto,
  CyberAssessmentStatus,
} from '@@types/api.types';
import { setReturnToSearchParam, useSafeReturnTo } from '@hooks/return-to.hook';
import { useGetCyberAssessment } from '@queries/cyber-assessment.query';
import {
  type CyberAssessmentQuestionLike,
  buildCyberAssessmentAnswerResponseByScrdfCode,
  buildCyberAssessmentShellModel,
  resolveDisplayedCyberAssessmentQuestionIndex,
} from '@utils/cyber-assessment/model.util';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type QuestionState = {
  index: number;
  sectionId?: string;
};

type UseCyberAssessmentQuestionPageModelResult = {
  breadcrumbHref: string;
  currentAnswer: CyberAssessmentAnswerSelectDto | undefined;
  currentChapter: NonNullable<ReturnType<typeof buildCyberAssessmentShellModel>>['chapters'][number];
  currentQuestion: CyberAssessmentQuestionLike;
  currentSection: NonNullable<ReturnType<typeof buildCyberAssessmentShellModel>>['sectionsById'][string];
  currentSectionIndex: number;
  currentSectionIndexInChapter: number;
  cyberAssessment: CyberAssessmentSelectDto;
  goToNext: () => void;
  goToPrevious: () => void;
  isReadOnly: boolean;
  questionLabels: { isComplete: boolean; label: string }[];
  safeCurrentQuestionIndex: number;
  setQuestionIndex: (index: number) => void;
  technologyId: string;
};

export const useCyberAssessmentQuestionPageModel = (): UseCyberAssessmentQuestionPageModelResult | null => {
  const { cyberAssessmentId, sectionId, technologyId } = useParams();
  const navigate = useNavigate();
  const { data: cyberAssessment } = useGetCyberAssessment(cyberAssessmentId);
  const { returnTo } = useSafeReturnTo();
  const shellModel = useMemo(
    () => (cyberAssessment ? buildCyberAssessmentShellModel(cyberAssessment) : null),
    [cyberAssessment],
  );
  const answerResponseByScrdfCode = useMemo(
    () => buildCyberAssessmentAnswerResponseByScrdfCode(cyberAssessment?.answers ?? []),
    [cyberAssessment?.answers],
  );
  const currentSection = sectionId && shellModel ? shellModel.sectionsById[sectionId] : undefined;
  const currentSectionIndex =
    sectionId && shellModel ? shellModel.sectionOrder.findIndex((item) => item === sectionId) : -1;
  const currentChapter = shellModel?.chapters.find((chapter) =>
    chapter.sections.some((section) => section.id === sectionId),
  );
  const currentSectionIndexInChapter = currentChapter?.sections.findIndex((section) => section.id === sectionId) ?? 0;
  const currentSectionQuestions = currentSection?.questions ?? [];
  const defaultQuestionIndex = useMemo(() => {
    const firstUnansweredIndex = currentSectionQuestions.findIndex((question) => {
      return !answerResponseByScrdfCode.get(question.id);
    });

    return firstUnansweredIndex === -1 ? 0 : firstUnansweredIndex;
  }, [answerResponseByScrdfCode, currentSectionQuestions]);
  const [questionState, setQuestionState] = useState<QuestionState>({ index: 0 });
  const previousSectionIdRef = useRef(sectionId);

  useEffect(() => {
    if (previousSectionIdRef.current !== sectionId) {
      previousSectionIdRef.current = sectionId;
      setQuestionState({ index: defaultQuestionIndex, sectionId });
    }
  }, [defaultQuestionIndex, sectionId]);

  if (
    !cyberAssessment ||
    !currentChapter ||
    !currentSection ||
    currentSectionIndex === -1 ||
    !shellModel ||
    !technologyId
  ) {
    return null;
  }

  const breadcrumbSearchParams = new URLSearchParams();

  breadcrumbSearchParams.set('chapterId', currentChapter.id);
  setReturnToSearchParam({ returnTo, searchParams: breadcrumbSearchParams });
  const breadcrumbHref = `/technology/${technologyId}/cyber-assessment/${cyberAssessment.id}?${breadcrumbSearchParams.toString()}`;
  const safeCurrentQuestionIndex = resolveDisplayedCyberAssessmentQuestionIndex({
    currentSectionId: currentSection.id,
    defaultQuestionIndex,
    questionCount: currentSection.questions.length,
    stateQuestionIndex: questionState.index,
    stateSectionId: questionState.sectionId,
  });
  const currentQuestion = currentSection.questions[safeCurrentQuestionIndex];
  const currentAnswer = cyberAssessment.answers?.find((answer) => answer.questionId === currentQuestion?.id);
  const questionLabels = currentSection.questions.map((question, index) => ({
    isComplete: Boolean(answerResponseByScrdfCode.get(question.id)),
    label: (index + 1).toString(),
  }));
  const navigateToSection = (nextSectionId: string): void => {
    navigate(
      `/technology/${technologyId}/cyber-assessment/${cyberAssessment.id}/section/${nextSectionId}${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`,
    );
  };
  const goToAssessmentOverview = (): void => {
    navigate(
      `/technology/${technologyId}/cyber-assessment/${cyberAssessment.id}${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`,
    );
  };

  return {
    breadcrumbHref,
    currentAnswer,
    currentChapter,
    currentQuestion,
    currentSection,
    currentSectionIndex,
    currentSectionIndexInChapter,
    cyberAssessment,
    goToNext: () => {
      const nextQuestionIndex = safeCurrentQuestionIndex + 1;

      if (nextQuestionIndex < currentSection.questions.length) {
        setQuestionState({
          index: nextQuestionIndex,
          sectionId: currentSection.id,
        });
        return;
      }

      const nextSectionId = shellModel.sectionOrder[currentSectionIndex + 1];

      if (!nextSectionId) {
        goToAssessmentOverview();
        return;
      }

      navigateToSection(nextSectionId);
    },
    goToPrevious: () => {
      const previousQuestionIndex = safeCurrentQuestionIndex - 1;

      if (previousQuestionIndex >= 0) {
        setQuestionState({
          index: previousQuestionIndex,
          sectionId: currentSection.id,
        });
        return;
      }

      const previousSectionId = shellModel.sectionOrder[currentSectionIndex - 1];

      if (!previousSectionId) {
        return;
      }

      navigateToSection(previousSectionId);
    },
    isReadOnly: cyberAssessment.status !== CyberAssessmentStatus.DRAFT,
    questionLabels,
    safeCurrentQuestionIndex,
    setQuestionIndex: (index: number) => {
      setQuestionState({
        index,
        sectionId: currentSection.id,
      });
    },
    technologyId,
  };
};
